import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useAuthContext } from "../context/AuthContext";
import AdminMenu from "./AdminMenu";
import "../styles/navbar.css";

const NavItem: React.FC<React.PropsWithChildren<{ to: string }>> = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + "/");
  return (
    <NavLink
      to={to}
      className={
        "px-2 py-1 rounded-md transition whitespace-nowrap " +
        (isActive ? "text-indigo-600 font-semibold" : "text-gray-700 hover:text-gray-900")
      }
    >
      {children}
    </NavLink>
  );
};

const Navbar: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [adminOpen, setAdminOpen] = React.useState(false);
  const location = useLocation();

  const handleLogout = async () => {
    try {
      console.log('Logout clicked - signing out...');
      await logout();
      console.log('Logout complete - navigating to home...');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  React.useEffect(() => {
    setMobileOpen(false);
    setAdminOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-lg font-extrabold tracking-tight text-indigo-600 hover:text-indigo-700"
            >
              QuickQuiz
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <NavItem to="/host">Host a Quiz</NavItem>
              <NavItem to="/join">Join a Quiz</NavItem>
              <NavItem to="/help">Help</NavItem>
              {isAdmin && (
                <div className="relative">
                  <button
                    className="px-2 py-1 text-gray-700 hover:text-gray-900 rounded-md whitespace-nowrap"
                    onClick={() => setAdminOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={adminOpen}
                  >
                    Admin ▾
                  </button>
                  <AdminMenu isOpen={adminOpen} onClose={() => setAdminOpen(false)} />
                </div>
              )}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <span className="qq-user-chip" title={user.email ?? undefined}>
                  {user.email}
                  {isAdmin && <span className="qq-admin-badge">Admin</span>}
                </span>
                <button
                  onClick={handleLogout}
                  className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50"
              >
                Sign In
              </Link>
            )}
          </div>
          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100"
            aria-label="Open menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t py-2">
            <div className="flex flex-col gap-1">
              <NavItem to="/host">Host a Quiz</NavItem>
              <NavItem to="/join">Join a Quiz</NavItem>
              <NavItem to="/help">Help</NavItem>
              {isAdmin && (
                <>
                  <NavItem to="/admin">Dashboard</NavItem>
                  <NavItem to="/admin/logs">Logs</NavItem>
                  <NavItem to="/admin/users">User Management</NavItem>
                </>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between">
              {user ? (
                <>
                  <span className="qq-user-chip" title={user.email ?? undefined}>
                    {user.email}
                    {isAdmin && <span className="qq-admin-badge">Admin</span>}
                  </span>
                  <button
                    onClick={logout}
                    className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="rounded-md border px-3 py-1 text-sm hover:bg-gray-50"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;

